/**
 * IconFilterModal - Modal single-select para filtrar por icono de clasificacion
 * Estilo similar a IconSelectorModal pero con colores diferentes para indicar filtro
 */

import { useEffect, useState } from 'react'
import RatingIcon, { ICON_ROWS, NOMBRE_MAP } from './RatingIcon'
import { iconsAPI } from '../../services/api'

// Cicatrices filtrables (un solo badge por prospecto): red/green usan ♻️ SVG; tombstone/zombie usan emoji.
const RECYCLE_SVG = (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5" />
    <path d="M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12" />
    <path d="m14 16-3 3 3 3" />
    <path d="M8.293 13.596 7.196 9.5 3.1 10.598" />
    <path d="m9.344 5.811 1.093-1.892A1.83 1.83 0 0 1 11.985 3a1.784 1.784 0 0 1 1.546.888l3.943 6.843" />
    <path d="m13.378 9.633 4.096 1.098 1.097-4.096" />
  </svg>
)
const CICATRICES = [
  { id: 'cicatriz_red', color: 'text-red-600 dark:text-red-500', node: RECYCLE_SVG, title: 'Descuidados ahora (sin gestión ≥2 días hábiles)' },
  { id: 'cicatriz_green', color: 'text-green-600 dark:text-green-500', node: RECYCLE_SVG, title: 'Rescatados tras descuido' },
  { id: 'cicatriz_tombstone', color: '', node: <span className="text-xl leading-none">🪦</span>, title: 'Lápida: cayeron en Atrasados (vencido ≥2 días) sin gestión' },
  { id: 'cicatriz_zombie', color: '', node: <span className="text-xl leading-none">🧟</span>, title: 'Zombie: muertos revividos (reagendados con nota)' },
]

export default function IconFilterModal({ currentFilter, onSelect, onClose, scopeFilters }) {
  const [counts, setCounts] = useState({})

  useEffect(() => {
    iconsAPI.getCounts(scopeFilters || {})
      .then(r => {
        const normalized = {}
        for (const [key, n] of Object.entries(r || {})) {
          const canonical = NOMBRE_MAP[key] || key
          normalized[canonical] = (normalized[canonical] || 0) + n
        }
        setCounts(normalized)
      })
      .catch(() => {
        setCounts({})
      })
  }, [JSON.stringify(scopeFilters)])
  const handleSelect = (iconId) => {
    onSelect(iconId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-dark-alt rounded-2xl shadow-2xl w-full max-w-[675px] mx-4 overflow-hidden">
        {/* Header - purple gradient to distinguish from selector modal */}
        <div className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 px-6 py-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="3 4 21 4 14 14 14 20 10 22 10 14 3 4" />
            </svg>
            Filtrar por icono
          </h3>
          <p className="text-white/80 text-sm mt-1">Selecciona un icono para mostrar solo esos contactos</p>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="space-y-3">
            {ICON_ROWS.map((row, rowIdx) => (
              <div key={rowIdx} className="flex gap-2 justify-start flex-wrap">
                {row.map(icon => {
                  const isSelected = currentFilter === icon.id
                  return (
                    <button
                      key={icon.id}
                      onClick={() => handleSelect(icon.id)}
                      className={`relative p-1.5 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                        isSelected
                          ? 'bg-gradient-to-br from-purple-200 to-fuchsia-200 border-2 border-purple-500 shadow-lg dark:from-purple-900/50 dark:to-fuchsia-900/50'
                          : 'bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-500 hover:shadow-md'
                      }`}
                      title={icon.label}
                    >
                      <RatingIcon iconId={icon.id} size="lg" />
                      <span className={`absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 min-w-[20px] h-[20px] px-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center shadow ${counts[icon.id] > 0 ? '' : 'opacity-40'}`}>
                        {counts[icon.id] ?? 0}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))}

            {/* Sin icono + cicatrices */}
            <div className="pt-3 mt-2 border-t border-gray-200 dark:border-gray-700 flex items-stretch gap-2">
              <button
                onClick={() => handleSelect('none')}
                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 ${
                  currentFilter === 'none'
                    ? 'bg-gradient-to-r from-purple-200 to-fuchsia-200 text-purple-900 border-2 border-purple-500 dark:from-purple-900/50 dark:to-fuchsia-900/50 dark:text-purple-200'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-slate-600 hover:bg-gray-200 dark:hover:bg-slate-600'
                }`}
              >
                <span>{"—"} Sin icono</span>
                <span className="min-w-[22px] px-1.5 h-[20px] rounded-full bg-fuchsia-600 text-white text-[11px] font-bold inline-flex items-center justify-center">{counts['none'] ?? 0}</span>
              </button>
              {CICATRICES.map(c => {
                const isSel = currentFilter === c.id
                return (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    title={c.title}
                    className={`relative px-2.5 py-2.5 rounded-xl border-2 transition transform hover:scale-105 active:scale-95 ${
                      isSel
                        ? 'bg-gradient-to-br from-purple-200 to-fuchsia-200 border-purple-500 shadow-lg dark:from-purple-900/50 dark:to-fuchsia-900/50'
                        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-500'
                    }`}
                  >
                    <span className={c.color}>{c.node}</span>
                    <span className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 min-w-[20px] h-[20px] px-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center shadow">{counts[c.id] ?? 0}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
